import Cookies from 'js-cookie';
import { pubSub } from '@utils/PubSub';
import { fetchStorefront, clean } from '@utils/GraphQLClient';
import { loadCart, closeCart } from '@utils/global-functions';
import { getDYOptions } from '@utils/dyResources';
import { UPSELL_ITEM, PRODUCT_RESPONSE_FROM_HANDLE } from './Queries';

const headers = async (handle, query) => {
  return {
    variables: {
      handle,
    },
    query,
  };
};

export const actions = {
  fetchWindow() {
    const global = window?.Brooklinen?.promo ?? {};
    this.swd = global?.discounts?.swd ?? false;
    this.tiers = global?.discounts?.tiers?.enabled
      ? global?.discounts?.tiers
      : false;
    this.gwp = global?.gwp ?? false;
    this.mode = window?.Brooklinen?.mode ?? 'production';
    this.swd_tags = global?.discounts?.swd?.tags.split(',') ?? [];
    this.tieredDiscount = global?.discounts?.tiers ?? this.tieredDiscount;
    this.global = global;
    if (this.gwp) {
      this.gwp.active = false;
      this.gwp.added = false;
    }
    this.promoReady = this.swd !== false;
  },
  async fetchEndpoint() {
    const response = await fetch('/cart?view=endpoint');
    return response.json();
  },
  /**
   * Fetches upsell product data from Dynamic Yield's choose endpoint,
   * then resolves each recommended handle against the Shopify Storefront API.
   * @returns {Promise<Object[]>} Array of product objects ready for the Upsell component.
   */
  async fetchUpsellItems() {
    const options = await getDYOptions();
    const res = await fetch(
      'https://direct.dy-api.com/v2/serve/user/choose',
      options
    );
    const data = await res.json();

    const choice = data?.choices?.find((c) => c.name === 'You May Also Like');
    const payload = choice?.variations?.[0]?.payload?.data;
    const slots = payload?.slots ?? [];

    this.upsell.decisionId = choice?.decisionId ?? null;
    this.upsell.variationId = choice?.variations?.[0]?.id ?? null;

    const items = await Promise.all(
      slots.map(async (slot) => {
        const handle = slot?.productData?.group_id;
        if (!handle) return null;
        const request = await headers(handle, UPSELL_ITEM);
        const response = await fetchStorefront(request);
        const product = clean(response?.data?.product);
        if (!product) return null;
        product.dySlotId = slot?.slotId ?? null;
        return product;
      })
    );

    this.upsell.title = payload?.custom?.title ?? null;
    return items.filter(Boolean);
  },
  async setAttributes() {
    const attributes = new FormData();
    attributes.append('attributes[online]', 'true');

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      body: attributes,
    });

    return response.json();
  },
  async onChange() {
    try {
      await this.setCartAbandonment();
    } catch (err) {
      throw new Error(`Cart Abandonment Failed: ${err}`);
    }

    try {
      await this.setCartCheckoutUrl();
    } catch (err) {
      throw new Error(`Checkout URL Failed: ${err}`);
    }
  },
  async setCartAbandonment() {
    const items = this.items;
    const hasCookie = Cookies.get('_bl_prod__checkout-token') ?? false;

    if (items.length <= 0 || hasCookie) return;

    const response = await fetch('/cart.json');
    const data = await response.json();
    const token = data.token;

    Cookies.set('_bl_prod__checkout-token', token, { expires: 21 });
    return;
  },
  async setCartCheckoutUrl() {
    const items = this.items;
    const hasInstance = Cookies.get('_bl_prod__checkout-instance') ?? false;
    if (items.length <= 0 || hasInstance) return;

    const checkout = await fetch('/cart?checkout=Checkout', { method: 'POST' });
    const checkoutInstance = checkout.url;

    Cookies.set('_bl_prod__checkout-instance', checkoutInstance, {
      expires: 21,
    });

    return;
  },
  pushCartToWindow(context) {
    window.dataLayer = window.dataLayer || [];

    const items = context.map((item) => {
      // TODO: in my next PR
      // let bundle = item.components.properties?._product_bundle_variant || null;
      return {
        id: `${item.id}_${item.variant_id}`,
        category: item.product_type,
        product_id: item.id,
        variant_id: item.variant_id,
        title: item.title,
        variant: item.variant_title,
        price: item.price,
        quantity: item.quantity,
        // product_bundle_variant_id: bundle,
        sku: item.sku,
      };
    });

    window.dataLayer.push({
      event: 'cart_contents',
      ecommerce: {
        cartResponse: [...items],
      },
    });
  },
  async displayCart(open = true) {
    const opening = open ?? true;
    if (!opening) return closeCart();

    const response = await this.fetchEndpoint();
    this.items = response.items;
    this.note = response.note;
    this.subtotal = response.total;
    this.pushCartToWindow(this.items);
    return opening ? loadCart() : closeCart();
  },
  cartListeners() {
    pubSub.subscribe('cart:request', () => this.displayCart(true));
  },
  async addGiftNote(message = '') {
    const removing = message.length <= 0;
    const quantity = removing ? '0' : '1';
    const properties = { message: message, _freeGift: true };
    const id = this.gift_note.toString();
    const payload =
      {
        id,
        quantity,
        properties,
      } ?? {};

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
      body: JSON.stringify({ note: message }),
    });

    const createEvent = (item) =>
      new CustomEvent('theme:add-to-cart', {
        detail: item,
      });

    const event = createEvent(payload);

    return removing ? this.fetchEndpoint() : document.dispatchEvent(event);
  },
  addTote() {
    const { mode, global } = this;
    const tote = global?.tote ?? false;
    if (!tote) return;

    const hasTote = this.totePresentInCart ?? false;
    const toteHasBeenRemoved =
      sessionStorage.getItem('toteManuallyRemoved') ?? false;
    if (toteHasBeenRemoved || hasTote) return;

    const variant = tote.env[mode].variant;
    const quantity = 1;

    const createEvent = (item) =>
      new CustomEvent('theme:add-to-cart', {
        detail: item,
      });

    const payload = {
      id: variant.toString(),
      quantity: quantity,
      properties: { _isGiftCard: false, _freeGift: true },
    };
    const event = createEvent(payload);
    document.dispatchEvent(event);
  },
  removeTote() {
    const { mode, global } = this;
    const tote = global?.tote ?? false;
    if (!tote) return;

    const hasTote = this.totePresentInCart ?? false;
    if (!hasTote) return;
    const variant = tote.env[mode].variant;
    const quantity = 0;

    const createEvent = (item) =>
      new CustomEvent('theme:add-to-cart', {
        detail: item,
      });

    const payload = {
      id: variant.toString(),
      quantity: quantity,
      properties: { _isGiftCard: false, _freeGift: true },
    };
    const event = createEvent(payload);
    document.dispatchEvent(event);
  },
  handleToteInteraction() {
    sessionStorage.setItem('toteManuallyRemoved', true);
  },
  async checkToteStatus() {
    const { global } = this;
    const tote = global?.tote ?? false;

    if (!tote) return (this.global.tote = false);
    const handle = tote?.handle ?? false;

    if (!handle) return (this.global.tote = false);

    const request = {
      variables: { handle },
      query: PRODUCT_RESPONSE_FROM_HANDLE,
    };
    const response = await fetchStorefront(request).then(
      (res) => res.data.product
    );
    if (!response) return (this.global.tote = false);
    return;
  },
  displayGWP() {
    this.gwp.active = true;
  },
  async removeGWP() {
    this.gwp.active = false;
    const cart = await this.fetchEndpoint();
    const items = cart.items;
    const giftWithPurchase = items.find((item) =>
      Object.keys(item.properties).includes('_gift-with-purchase')
    );
    if (giftWithPurchase) {
      const id = giftWithPurchase.key;
      const quantity = 0;

      document.dispatchEvent(
        new CustomEvent('theme:add-to-cart', {
          detail: {
            id,
            quantity,
          },
        })
      );
      this.gwp.added = false;
    }
  },
  gwpAdded() {
    this.gwp.added = true;
    this.gwp.active = false;
  },
};
